import { cx } from '@bangle.io/base-utils';
import {
  type EditorView,
  extractHeadings,
} from '@bangle.io/prosemirror-plugins';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ImageLightbox } from './components/image-lightbox';
import { TocRail } from './components/toc-rail';
import { createReadonlyMarkdownView } from './readonly-markdown-view';

const EDITOR_PROSE_CLASS = cx(
  'ProseMirror box-border min-h-0 min-w-0 max-w-full outline-0 outline-hidden',
  '[&_:not(pre)_code]:rounded-md [&_:not(pre)_code]:bg-muted/40 [&_:not(pre)_code]:px-1.5 [&_:not(pre)_code]:py-0.5 [&_:not(pre)_code]:font-mono',
  "[&_pre]:bg-muted/30 [&_span[data-mention='tag']]:text-primary [&_span[data-mention='user']]:text-accent",
);

export type ReadonlyMarkdownProps = {
  markdown: string;
  className?: string;
  allowCopy?: boolean;
  resolveImageSrc?: (src: string) => string;
  /**
   * Show the same heading rail readers of the editor get. Off by default so
   * embedded read-only previews stay uncluttered.
   */
  showToc?: boolean;
};

/**
 * Renders Markdown as a non-editable WYSIWYG ProseMirror document using the
 * same schema as the note editor. Safe to mount outside CoreServiceProvider.
 */
export function ReadonlyMarkdown({
  markdown,
  className,
  allowCopy = true,
  resolveImageSrc,
  showToc = false,
}: ReadonlyMarkdownProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [view, setView] = useState<EditorView | null>(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(
    null,
  );

  const onOpenImage = useCallback((src: string, alt: string) => {
    setLightbox({ src, alt });
  }, []);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) {
      return;
    }

    const nextView = createReadonlyMarkdownView({
      domNode: mount,
      markdown,
      resolveImageSrc,
      onOpenImage,
    });
    setView(nextView);

    return () => {
      nextView.destroy();
      setView(null);
    };
  }, [markdown, resolveImageSrc, onOpenImage]);

  // The read-only document never changes after it mounts, so the outline is
  // derived straight from the doc instead of running the editor's live
  // heading-watch plugin (which publishes into the app's Jotai store).
  const headings = useMemo(
    () => (view ? extractHeadings(view.state.doc) : undefined),
    [view],
  );

  return (
    <>
      <div
        ref={mountRef}
        className={cx(EDITOR_PROSE_CLASS, className)}
        style={allowCopy ? undefined : { userSelect: 'none' }}
        onCopy={
          allowCopy
            ? undefined
            : (event) => {
                event.preventDefault();
              }
        }
      />
      {showToc ? <TocRail editorView={view} headings={headings} /> : null}
      <ImageLightbox
        open={Boolean(lightbox)}
        src={lightbox?.src ?? null}
        alt={lightbox?.alt}
        onOpenChange={(open) => {
          if (!open) setLightbox(null);
        }}
      />
    </>
  );
}
